import { enableProdMode, NgZone } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { Workbox, WorkboxLifecycleWaitingEvent } from 'workbox-window';
import { SnackbarService } from './app/core/services/snackbar/snackbar.service';
import * as Sentry from '@sentry/angular';

if (environment.production) {
  enableProdMode();
}

function loadServiceWorker(snackbar: SnackbarService, zone: NgZone) {
  if ('serviceWorker' in navigator && environment.enableServiceWorker) {
    const wb = new Workbox('/sw.js');

    // Claim client on first sw install
    wb.addEventListener('activated', async (event) => {
      if (!event.isUpdate) {
        wb.messageSW({ type: 'CLIENTS_CLAIM' });
      }
    });

    // Listener that will prompt the user to refresh
    // when a new version of the service worker is detected.
    wb.addEventListener('waiting', (event) => {
      console.log('New service worker waiting to activate:', event);
      showSkipWaitingPrompt(event);
    });

    // Function to show the prompt
    const showSkipWaitingPrompt = async (
      event: WorkboxLifecycleWaitingEvent
    ) => {
      console.log('showSkipWaitingPrompt called with event:', event);
      // Listener that will reload the page after the user accepts the update
      wb.addEventListener('controlling', () => {
        window.location.reload();
      });

      // Open the update snackbar
      zone.run(() => {
        snackbar.openUpdateSnackBar().subscribe({
          next: (result) => {
            // If user accepted, tell the service worker to skipWaiting
            if (result.dismissedByAction) {
              wb.messageSkipWaiting();
            }
          },
        });
      });
    };

    wb.register().then((registration) => {
      // Registration was successful
      console.log(
        'ServiceWorker registration successful with scope: ',
        registration?.scope
      );
      // Check if there is an update available every hour
      setInterval(() => {
        wb.update().then((result) => {
          // console.log('checking for sw updates');
        });
      }, 1000 * 60 * 5);
    });
  }
}

async function requestPersistentStorage() {
  if (navigator.storage && navigator.storage.persist) {
    const isPersisted = await navigator.storage.persisted();

    if (!isPersisted) {
      const granted = await navigator.storage.persist();
    }
  }
}

Sentry.init({
  dsn: 'https://9c57a87017a0800c0b69e66c3e7b1bed@o4509151958925312.ingest.us.sentry.io/4509151961939968',
  allowUrls: ['localhost', 'accutrax.biz'],
  environment: environment.name,
  beforeSend(event, hint) {
    const error = hint?.originalException;
    const route = event.tags?.['route']?.toString();
    // console.log('Sentry event:', event);
    // console.log('Sentry hint:', hint);
    // console.log('Sentry error:', error);

    // Filter common network error types
    if (
      error instanceof TypeError &&
      /network|failed to fetch|load failed/i.test(error.message)
    ) {
      return null;
    }

    if ((error as any)?.status === 0) {
      return null;
    }

    // Filter out IndexedDB DOMExceptions
    if (
      error instanceof DOMException &&
      (error.message.includes('The database connection is closing') ||
        error.message.includes('Connection is closing') ||
        error.message.includes(
          'The transaction was aborted, so the request cannot be fulfilled.'
        ))
    ) {
      return null;
    }

    // Filter out all Firebase errors using event.exception.values
    if (
      event.exception?.values?.some(
        (val) =>
          val.type?.includes('FirebaseError') ||
          val.value?.includes('Firebase:') ||
          val.value?.includes('Firebase')
      )
    ) {
      return null;
    }

    // Filter out all events that are thrown on the login page
    if (route === '/auth/login') {
      return null;
    }

    return event; // send everything else
  },
  // Disable Performance Monitoring
  tracesSampleRate: 0,

  // Disable Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  // Disable Breadcrumbs and Instrumentation
  integrations: (integrations) =>
    integrations.filter(
      (integration) =>
        ![
          'Breadcrumbs',
          'GlobalHandlers',
          'TryCatch',
          'HttpContext',
          'InboundFilters',
          'LinkedErrors',
        ].includes(integration.name)
    ),

  // Optional: avoid console performance impact
  beforeBreadcrumb: () => null,
});

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .then((ref) => {
    const snackbar = ref.injector.get(SnackbarService);
    const zone = ref.injector.get(NgZone);
    loadServiceWorker(snackbar, zone);
    requestPersistentStorage(); // Call the function here
  })
  .catch((err) => console.error(err));
